import React, {
  useContext,
  useReducer,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import fetchAPI from '../../utils/fetchAPI';
import FormBuilderShareEmailSettingsView from '../view/FormBuilderShareEmailSettingsView';
import { FormBuilderContext } from './FormBuilderEngine';

const getEmailConfigurationSender = configBlock => {
  if (!configBlock) {
    return undefined;
  }

  const senderAddress = configBlock.value;
  const regex = /.*(?=\@)/gi;
  const matches = senderAddress.match(regex);
  return matches[0];
};

const getEmailConfigurationDomain = configBlock => {
  if (!configBlock) {
    return undefined;
  }

  const senderAddress = configBlock.value;
  const regex = /@(.*)/gi;
  const matches = regex.exec(senderAddress);
  return matches[1];
};

const emailsConfigReducer = (state, action) => {
  const { type, value, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const FormBuilderShareEmailSettingsEngine = ({ formId: _formId }) => {
  const t = useTranslations();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { form } = useContext(FormBuilderContext);
  const formId = useMemo(() => _formId || (form && form.id) || '', [
    form,
    _formId
  ]);

  const [emailConfigs, dispatchEmailConfigs] = useReducer(emailsConfigReducer, {
    default: {},
    currentConfig: null
  });

  const [fromEmailAddressDomain, setFromEmailAddressDomain] = useState('');
  const [isEmailFromReadOnly, setIsEmailFromReadOnly] = useState(true);
  const [fromEmailAddress, setFromEmailAddress] = useState('');
  const [isEmailFromOk, setIsEmailFromOk] = useState(true);

  useEffect(() => {
    fetchAPI(
      '/configurations/v1/modules/forms?order=id asc&query=[[{"field":"\'configName\'","op":"=","value":"system_settings"},{"field":"\'code\'","op":"ILIKE","value":"from_email%"}]]'
    ).then(r => {
      const DEF_DOMAIN = 'omnitrack.vinciworks.com';
      const configs = r.configs;
      let defaultConfig = {};
      let currentConfig = null;
      configs.forEach(conf => {
        if (!conf.assetId) {
          defaultConfig = conf;
        } else if (conf.assetId === formId) {
          currentConfig = conf;
        }
      });

      if (!currentConfig) {
        // no configurations set for this form
        setFromEmailAddress(getEmailConfigurationSender(defaultConfig));
      } else {
        setFromEmailAddress(getEmailConfigurationSender(currentConfig));
      }

      // at the moment we only support 1 domain so we can assume both
      // the defult config and the current config uses the same domain
      const domain = getEmailConfigurationDomain(defaultConfig);
      if (domain !== DEF_DOMAIN) {
        setIsEmailFromReadOnly(false);
      }
      setFromEmailAddressDomain(domain);

      dispatchEmailConfigs({
        type: 'update',
        payload: { default: defaultConfig, currentConfig }
      });
    });
  }, []);

  const onEmailSenderInputBlur = useCallback(() => {
    const emailConfigBlock = emailConfigs.currentConfig || emailConfigs.default;

    if (
      !!!fromEmailAddress ||
      fromEmailAddress.trim() === getEmailConfigurationSender(emailConfigBlock)
    ) {
      // they deleted the value - lets get it back!
      const oldSenderAddress = getEmailConfigurationSender(emailConfigBlock);
      setFromEmailAddress(`${oldSenderAddress}`);
      return;
    }

    // validate input:
    const fullAddress = `${fromEmailAddress.trim()}@${getEmailConfigurationDomain(
      emailConfigBlock
    )}`;
    const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const results = re.test(String(fullAddress).toLowerCase());
    if (!results) {
      setIsEmailFromOk(false);
      return;
    }

    let method = 'POST';
    let url = '/configurations/v1/modules/forms';
    const body = {
      ...emailConfigBlock,
      value: fullAddress,
      module: 'forms',
      assetId: formId,
      code: `from_email_${formId}`
    };
    if (emailConfigBlock.assetId) {
      method = 'PUT';
      url += `/${emailConfigBlock.id}`;
    }

    fetchAPI(url, {
      method,
      body,
      handleResponse: r => {
        setTimeout(() => {
          // so it won't conflict with on blur event
          openSnackbar(
            'success',
            t('form-builder.users.email_settings.success')
          );
        }, 200);
        if (method === 'POST') {
          const fullId = r.headers.map.location;
          const regex = /\w*\/(.*)/gi;
          const matches = regex.exec(fullId);
          body.id = matches[1];
        }
        dispatchEmailConfigs({
          type: 'update',
          payload: { currentConfig: body }
        });
      }
    });
  }, [emailConfigs, fromEmailAddress, formId]);

  const viewProps = {
    isEmailFromReadOnly,
    fromEmailAddress,
    isEmailFromOk,
    setIsEmailFromOk,
    onEmailSenderInputBlur,
    fromEmailAddressDomain,
    setFromEmailAddress
  };

  return <FormBuilderShareEmailSettingsView {...viewProps} />;
};

export default FormBuilderShareEmailSettingsEngine;
